// src/components/hall-prices/StandingQuantityModal.tsx
// Модалка выбора количества стоячих мест для одного заказа: у стоячего блока
// есть общая вместимость (например, 100 мест), и админ может продать в одном
// заказе только часть из неё — остаток остаётся доступным для будущих отдельных
// заказов (см. split-эндпоинт /hall-prices/seat-prices/{id}/split на бэкенде).
import React, { useEffect, useState } from "react";
import Card from "../ui/Card";
import Button from "../ui/Button";
import Input from "../ui/Input";
import Field from "../ui/Field";

interface StandingQuantityModalProps {
    isOpen: boolean;
    zoneLabel: string;
    available: number;
    isSubmitting: boolean;
    error?: string | null;
    onClose: () => void;
    onConfirm: (quantity: number) => void | Promise<void>;
}

const StandingQuantityModal: React.FC<StandingQuantityModalProps> = ({
    isOpen,
    zoneLabel,
    available,
    isSubmitting,
    error,
    onClose,
    onConfirm,
}) => {
    // По умолчанию — 1 место (а не вся вместимость): продажа блока целиком —
    // частный случай, админ сам увеличивает количество кнопкой "+" при необходимости.
    const [quantityValue, setQuantityValue] = useState("1");

    useEffect(() => {
        if (isOpen) {
            setQuantityValue("1");
        }
    }, [isOpen, available]);

    if (!isOpen) return null;

    const parsedQuantity = Number(quantityValue);
    const isValidQuantity =
        Number.isFinite(parsedQuantity) &&
        Number.isInteger(parsedQuantity) &&
        parsedQuantity >= 1 &&
        parsedQuantity <= available;

    const clampAndSet = (next: number) => {
        const clamped = Math.min(Math.max(next, 1), Math.max(available, 1));
        setQuantityValue(String(clamped));
    };

    return (
        <div className="modal-backdrop">
            <div className="modal-dialog">
                <Card>
                    <div className="editor-head">
                        <div>
                            <h3 className="card-title">Количество мест в заказ</h3>
                            <p className="card-text">{zoneLabel}</p>
                        </div>
                    </div>

                    <div className="scheme-editor-tool-properties" style={{ marginBottom: 16 }}>
                        <p className="scheme-editor-tool-properties-text">
                            Доступно сейчас: <strong>{available}</strong>
                        </p>
                        <p className="scheme-editor-tool-properties-hint">
                            Можно продать часть вместимости в этом заказе — остаток останется
                            доступным для следующих отдельных заказов.
                        </p>
                    </div>

                    <Field label="Количество" htmlFor="standing-quantity-value">
                        <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                            <Button
                                type="button"
                                variant="secondary"
                                onClick={() => clampAndSet(parsedQuantity - 1)}
                                disabled={isSubmitting || parsedQuantity <= 1}
                            >
                                −
                            </Button>

                            <Input
                                id="standing-quantity-value"
                                type="number"
                                min={1}
                                max={Math.max(available, 1)}
                                value={quantityValue}
                                onChange={(event) => setQuantityValue(event.target.value)}
                                style={{ textAlign: "center" }}
                            />

                            <Button
                                type="button"
                                variant="secondary"
                                onClick={() => clampAndSet(parsedQuantity + 1)}
                                disabled={isSubmitting || parsedQuantity >= available}
                            >
                                +
                            </Button>
                        </div>
                    </Field>

                    {!isValidQuantity ? (
                        <p className="card-text" style={{ color: "var(--danger, #e5484d)" }}>
                            Введите целое число от 1 до {available}.
                        </p>
                    ) : null}

                    {error ? (
                        <p className="card-text" style={{ color: "var(--danger, #e5484d)" }}>
                            {error}
                        </p>
                    ) : null}

                    <div className="editor-actions">
                        <Button type="button" variant="ghost" onClick={onClose} disabled={isSubmitting}>
                            Отмена
                        </Button>
                        <Button
                            type="button"
                            variant="primary"
                            onClick={() => onConfirm(parsedQuantity)}
                            disabled={isSubmitting || !isValidQuantity}
                        >
                            {isSubmitting ? "Оформляем..." : "Добавить в заказ"}
                        </Button>
                    </div>
                </Card>
            </div>
        </div>
    );
};

export default StandingQuantityModal;
